import { CommonService } from './../../../core/services/common/common.service';
import { Component, EventEmitter, Output, OnInit, OnDestroy, AfterViewInit, ElementRef, HostListener, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { User } from '@auth0/auth0-angular';
import { filter, Subject, takeUntil } from 'rxjs';
import { Auth0WrapperService, getCookie } from '@auth/bp-pf-auth';
import { environment } from '../../../../environments/environment';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { SupportComponent } from '@support/bp-pf-support';
import Quill from 'quill';
import { PermissionService } from '../../../modules/auth/services/permission.service';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, NgbTooltipModule, SupportComponent],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent implements OnInit, OnDestroy, AfterViewInit {

  userName: string = '';
  firstName: string = '';
  lastName: string = '';
  email: string = '';

  appId: string = environment.app_id;
  isSubSidebarOpen = false;
  isPinned = false;

  showProfileModal: boolean = false;

  manualOpen = false;
  currentUrl = '';
  showBpMenu = false;
  isPanelOpen = false;

  private panelObserver: MutationObserver | null = null;

  private destroy$ = new Subject<void>();
  permissionService = inject(PermissionService)

  @Output() loadingChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(private auth0: Auth0WrapperService, private commonService: CommonService, private router: Router, private el: ElementRef) { }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const supportEl = this.el.nativeElement.querySelector('lib-support');
    if (!supportEl) return;
    const panel = supportEl.querySelector('.support-panel');
    const isOpen = panel?.classList.contains('open');
    if (isOpen && !supportEl.contains(event.target as Node)) {
      const backdrop = supportEl.querySelector('.support-backdrop') as HTMLElement;
      if (backdrop) backdrop.click();
    }
  }

  ngAfterViewInit(): void {
    this.startPanelObserver();
  }

  private startPanelObserver(): void {
    this.panelObserver = new MutationObserver(() => {
      this.tryInjectQuill();
    });
    this.panelObserver.observe(document.body, { childList: true, subtree: true });
  }

  private tryInjectQuill(): void {
    const textarea = document.querySelector('#ticketDescription') as HTMLTextAreaElement;
    if (!textarea || textarea.dataset['quillInit']) return;

    textarea.dataset['quillInit'] = 'true';

    const formGroup = textarea.closest('.form-group') as HTMLElement;
    if (!formGroup) return;

    const editorContainer = document.createElement('div');
    editorContainer.id = 'quill-description-editor';
    formGroup.insertBefore(editorContainer, textarea);
    textarea.style.display = 'none';

    const quill = new Quill(editorContainer, {
      theme: 'snow',
      placeholder: 'Enter the Description',
      modules: {
        toolbar: [
          ['bold', 'italic'],
          [{ list: 'ordered' }, { list: 'bullet' }],
        ]
      }
    });

    quill.on('text-change', () => {
      const html = quill.root.innerHTML;
      const value = html === '<p><br></p>' ? '' : html;
      const setter = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value')?.set;
      if (setter) {
        setter.call(textarea, value);
        textarea.dispatchEvent(new Event('input', { bubbles: true }));
      }
    });

    const resetBtn = document.querySelector('lib-support .outline') as HTMLButtonElement;
    if (resetBtn) {
      resetBtn.addEventListener('click', () => {
        setTimeout(() => quill.setContents([]), 50);
      });
    }
  }

  ngOnInit() {
    this.getUser();


    // 👇 THIS IS THE MISSING PIECE
    this.currentUrl = this.router.url;

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.currentUrl = event.urlAfterRedirects;
      });

  }

  onReload(): void {
    window.location.reload();
  }

  openSubSidebar() {
    if (!this.manualOpen) {
      this.isSubSidebarOpen = true;
    }
  }

  closeSubSidebar() {
    if (!this.manualOpen && !this.isPinned) {
      this.isSubSidebarOpen = false;
    }
  }

  isRouteActive(route: string): boolean {
    return this.currentUrl.split('?')[0].includes(`/${route}`);
  }

  getUser() {
    const getTenantCodeFromCookie = getCookie(
      `${environment.app_id}_tenant_code`,
    );
    const email = getCookie(
      `${getTenantCodeFromCookie}_${environment.app_id}_email`,
    );

    if (email) {
      this.email = email;

      const [namePart, domain] = email.toLowerCase().split('@');

      // name
      const [first, last] = namePart.split('.');
      this.firstName = first
        ? first.charAt(0).toUpperCase() + first.slice(1)
        : '';
      this.lastName = last
        ? last.charAt(0).toUpperCase() + last.slice(1)
        : '';

      this.userName = `${this.firstName} ${this.lastName}`.trim();

      // 👇 DOMAIN CHECK (THIS IS THE KEY)
      this.showBpMenu = environment.isBPAllSubmissionEnabledForAll ||
        (domain === 'bluepond.ai' ||
          domain === 'bluepond.com');

    } else {
      this.firstName = 'Guest';
      this.lastName = '';
      this.email = '';
      this.userName = 'Guest';
      this.showBpMenu = false;
    }
  }

  public async logout() {
    this.loadingChange.emit(true);
    try {
      this.auth0.logoutWithRedirect();
      localStorage.removeItem('workbench-list-filters');
      localStorage.removeItem('bp-all-submissions-filters');
      localStorage.removeItem('dashboard-management-filters');
      this.clearPersonalizationCache();

      await this.clearCache('workbench-list-submissions-db', 'submissions-store', 1);
      await this.clearCache('bp-all-submissions-db', 'submissions-store', 1);
      await this.clearCache('dashboard-management-db', 'submissions-store', 1);
    } catch (error) {
      this.loadingChange.emit(false);
    }
  }

  private clearPersonalizationCache(): void {
    // TODO: This is currently used for testing purposes only. Remove this method once the actual implementation is complete.
    Object.keys(localStorage)
      .filter((key) => key.startsWith('bp_personalization'))
      .forEach((key) => localStorage.removeItem(key));
  }

  navigateTo(path: string): void {
    this.commonService.navigateToURLWithtenantCode(path);
  }

  toggleProfileModal() {
    this.showProfileModal = !this.showProfileModal;
  }

  closeProfileModal() {
    this.showProfileModal = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.panelObserver) {
      this.panelObserver.disconnect();
      this.panelObserver = null;
    }
  }

  private openDB(DB_NAME: string, STORE_NAME: string, DB_VERSION: number): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'key' });
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  private async clearCache(DB_NAME: string, STORE_NAME: string, DB_VERSION: number): Promise<void> {
    try {
      const db = await this.openDB(DB_NAME, STORE_NAME, DB_VERSION);
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).clear();
    } catch (e) {
      console.warn('Cache clear failed', e);
    }
  }

}
